import * as RadixPopover from "@radix-ui/react-popover";
import { CalendarBlank, CaretDown, CaretLeft, CaretRight, Check, X } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/primitives/Button";
import { WarningAlertIcon, warningSurfaceTextStyle } from "@/components/ui/feedback/warningVisual";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding, floatingLayerClassName } from "@/components/ui/overlays/overlayPositioning";
import {
  CUSTOM_PRESET_ID,
  DAY_HEADS,
  MONTH_NAMES,
  SHORT_MONTH_NAMES,
  compareDay,
  daysInMonth,
  defaultDateRangePresets,
  defaultFormatDate,
  differenceInCalendarDays,
  getInitialCalendar,
  getPresetLabel,
  hasCompleteRange,
  normalizeRange,
  sameDay,
  shiftMonth,
  startOfDay
} from "./DateRangePicker.utils";
import type { DateRangePickerProps, DateRangePreset, DateRangePresetId, DateRangeValue } from "./DateRangePicker.types";

type CalendarMonthProps = {
  year: number;
  month: number;
  from?: Date;
  to?: Date;
  hovered?: Date;
  awaitingTo: boolean;
  isDateDisabled: (date: Date) => boolean;
  onSelectDate: (date: Date) => void;
  onHoverDate: (date?: Date) => void;
  onPrev: () => void;
  onNext: () => void;
  showPrev?: boolean;
  showNext?: boolean;
  className?: string;
};

function CalendarMonth({
  year,
  month,
  from,
  to,
  hovered,
  awaitingTo,
  isDateDisabled,
  onSelectDate,
  onHoverDate,
  onPrev,
  onNext,
  showPrev = true,
  showNext = true,
  className
}: CalendarMonthProps) {
  const firstDayOffset = new Date(year, month, 1).getDay();
  const totalDays = daysInMonth(year, month);
  const cells: Array<number | null> = Array(firstDayOffset).fill(null);

  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(day);
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const rangeEnd = to ?? (awaitingTo ? hovered : undefined);
  const range = from && rangeEnd ? normalizeRange(from, rangeEnd) : undefined;

  return (
    <div className={cn("bfrs-w-[252px] bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-3 bfrs-shadow-low", className)}>
      <div className="bfrs-mb-2 bfrs-flex bfrs-items-center bfrs-justify-between">
        {showPrev ? (
          <button
            type="button"
            className="bfrs-flex bfrs-size-7 bfrs-items-center bfrs-justify-center bfrs-rounded-md bfrs-text-text-muted bfrs-outline-none hover:bfrs-bg-primary-soft hover:bfrs-text-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus"
            onClick={onPrev}
            aria-label="Previous month"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
        ) : (
          <div className="bfrs-size-7" aria-hidden="true" />
        )}
        <div className="bfrs-flex bfrs-items-center bfrs-gap-1 bfrs-text-sm bfrs-font-bold bfrs-text-text-primary">
          {MONTH_NAMES[month]}, {year}
          <CaretDown size={11} weight="bold" className="bfrs-text-text-muted" />
        </div>
        {showNext ? (
          <button
            type="button"
            className="bfrs-flex bfrs-size-7 bfrs-items-center bfrs-justify-center bfrs-rounded-md bfrs-text-text-muted bfrs-outline-none hover:bfrs-bg-primary-soft hover:bfrs-text-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus"
            onClick={onNext}
            aria-label="Next month"
          >
            <CaretRight size={14} weight="bold" />
          </button>
        ) : (
          <div className="bfrs-size-7" aria-hidden="true" />
        )}
      </div>

      <div className="bfrs-mb-0.5 bfrs-grid bfrs-grid-cols-7 bfrs-border-b bfrs-border-border bfrs-pb-1.5">
        {DAY_HEADS.map((day, index) => (
          <div key={`${day}-${index}`} className="bfrs-text-center bfrs-text-xs bfrs-font-semibold bfrs-text-text-disabled">
            {day}
          </div>
        ))}
      </div>

      <div className="bfrs-grid bfrs-grid-cols-7">
        <div className="bfrs-col-span-7 bfrs-px-0.5 bfrs-py-1 bfrs-text-[11px] bfrs-font-bold bfrs-tracking-[0.12em] bfrs-text-text-disabled">
          {SHORT_MONTH_NAMES[month]}
        </div>
        {cells.map((day, index) => {
          if (!day) {
            return <div key={`blank-${index}`} className="bfrs-h-8" />;
          }

          const date = new Date(year, month, day);
          const selectedStart = sameDay(date, range?.from ?? from);
          const selectedEnd = sameDay(date, range?.to ?? to);
          const selected = selectedStart || selectedEnd;
          const inRange = Boolean(range && compareDay(date, range.from) > 0 && compareDay(date, range.to) < 0);
          const disabled = isDateDisabled(date);

          // Grey range band. Endpoints fill only the half pointing into the range;
          // in-range days fill the whole cell. Outer corners round at the range
          // ends and at each week-row / month edge so the band reads as soft pills.
          const col = index % 7;
          const hasRange = Boolean(range && compareDay(range.from, range.to) !== 0);
          const showBand = inRange || (hasRange && selected);
          let bandClass: string;
          if (hasRange && selectedStart) {
            bandClass = "bfrs-left-1/2 bfrs-right-0";
          } else if (hasRange && selectedEnd) {
            bandClass = "bfrs-left-0 bfrs-right-1/2";
          } else {
            bandClass = cn(
              "bfrs-inset-x-0",
              (col === 0 || day === 1) && "bfrs-rounded-l-lg",
              (col === 6 || day === totalDays) && "bfrs-rounded-r-lg"
            );
          }

          return (
            <div
              key={day}
              className="bfrs-relative bfrs-flex bfrs-h-8 bfrs-items-center bfrs-justify-center"
              onMouseEnter={() => onHoverDate(date)}
              onMouseLeave={() => onHoverDate(undefined)}
            >
              {showBand && (
                <div className={cn("bfrs-pointer-events-none bfrs-absolute bfrs-inset-y-[2px] bfrs-bg-primary-soft", bandClass)} aria-hidden="true" />
              )}
              <button
                type="button"
                disabled={disabled}
                onClick={() => onSelectDate(date)}
                aria-pressed={selected}
                aria-label={defaultFormatDate(date)}
                className={cn(
                  "bfrs-relative bfrs-z-10 bfrs-flex bfrs-size-7 bfrs-cursor-pointer bfrs-items-center bfrs-justify-center bfrs-text-[12px] bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus",
                  // Range endpoints square off the side that meets the band so they sit flush
                  // with it; the start keeps its left corners, the end keeps its right corners.
                  hasRange && selectedStart ? "bfrs-rounded-l-lg" : hasRange && selectedEnd ? "bfrs-rounded-r-lg" : "bfrs-rounded-lg",
                  selected && "bfrs-bg-primary bfrs-font-bold bfrs-text-primary-contrast bfrs-shadow-low ",
                  !selected && inRange && "bfrs-font-medium bfrs-text-text-primary",
                  !selected && !inRange && "bfrs-text-text-secondary hover:bfrs-bg-primary-soft hover:bfrs-text-text-primary",
                  disabled && "bfrs-cursor-not-allowed bfrs-text-text-disabled bfrs-opacity-40 hover:bfrs-bg-transparent hover:bfrs-text-text-disabled"
                )}
              >
                {day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DateRangePicker({
  className,
  value,
  defaultValue,
  onChange,
  presets = defaultDateRangePresets,
  placeholder = "Select date range",
  label,
  helperText,
  size = "md",
  disabled,
  clearable,
  minDate,
  maxDate,
  maxRangeDays,
  months = 2,
  align = "start",
  side = "bottom",
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  infoText,
  customPresetLabel = "Custom",
  applyLabel = "Apply",
  cancelLabel = "Cancel",
  clearLabel = "Clear",
  formatDate = defaultFormatDate,
  formatRange,
  triggerClassName,
  contentClassName,
  calendarClassName,
  ...props
}: DateRangePickerProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState<DateRangeValue>(defaultValue ?? {});
  const committedValue = value ?? uncontrolledValue;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const [activePreset, setActivePreset] = useState<DateRangePresetId>(committedValue.preset ?? CUSTOM_PRESET_ID);
  const [draftValue, setDraftValue] = useState<DateRangeValue>(committedValue);
  const [hoveredDate, setHoveredDate] = useState<Date | undefined>();
  const [awaitingTo, setAwaitingTo] = useState(false);
  const [leftCalendar, setLeftCalendar] = useState(() => getInitialCalendar(committedValue, months));
  const rightCalendar = useMemo(() => shiftMonth(leftCalendar.year, leftCalendar.month, 1), [leftCalendar]);

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange]
  );

  const commitValue = useCallback(
    (nextValue: DateRangeValue) => {
      if (value === undefined) {
        setUncontrolledValue(nextValue);
      }
      onChange?.(nextValue);
    },
    [onChange, value]
  );

  useEffect(() => {
    if (!open) return;
    setDraftValue(committedValue);
    setActivePreset(committedValue.preset ?? (hasCompleteRange(committedValue) ? CUSTOM_PRESET_ID : ""));
    setHoveredDate(undefined);
    setAwaitingTo(false);
    setLeftCalendar(getInitialCalendar(committedValue, months));
  }, [committedValue, months, open]);

  const triggerLabel = useMemo(() => {
    if (formatRange) {
      return formatRange(committedValue);
    }

    const presetLabel = getPresetLabel(presets, committedValue.preset);
    if (presetLabel && committedValue.preset !== CUSTOM_PRESET_ID) {
      return presetLabel;
    }

    if (hasCompleteRange(committedValue)) {
      return sameDay(committedValue.from, committedValue.to)
        ? formatDate(committedValue.from)
        : `${formatDate(committedValue.from)} - ${formatDate(committedValue.to)}`;
    }

    return placeholder;
  }, [committedValue, formatDate, formatRange, placeholder, presets]);

  const isDateDisabled = useCallback(
    (date: Date) => {
      const normalizedDate = startOfDay(date);
      const normalizedMin = minDate ? startOfDay(minDate) : undefined;
      const normalizedMax = maxDate ? startOfDay(maxDate) : undefined;

      if (normalizedMin && compareDay(normalizedDate, normalizedMin) < 0) return true;
      if (normalizedMax && compareDay(normalizedDate, normalizedMax) > 0) return true;
      if (awaitingTo && draftValue.from && maxRangeDays && differenceInCalendarDays(normalizedDate, draftValue.from) + 1 > maxRangeDays) return true;

      return false;
    },
    [awaitingTo, draftValue.from, maxDate, maxRangeDays, minDate]
  );

  const selectPreset = (preset: DateRangePreset) => {
    if (preset.disabled) return;

    const presetRange = preset.getRange();
    const range = normalizeRange(presetRange.from, presetRange.to);
    commitValue({ preset: preset.id, ...range });
    setOpen(false);
  };

  const startCustomSelection = () => {
    setActivePreset(CUSTOM_PRESET_ID);
    setDraftValue((current) => ({
      preset: CUSTOM_PRESET_ID,
      from: current.from,
      to: current.to
    }));
    setAwaitingTo(false);
    setHoveredDate(undefined);
    setLeftCalendar(getInitialCalendar(draftValue, months));
  };

  const selectDate = (date: Date) => {
    if (isDateDisabled(date)) return;

    const selected = startOfDay(date);

    if (!awaitingTo || !draftValue.from || draftValue.to) {
      setDraftValue({ preset: CUSTOM_PRESET_ID, from: selected, to: undefined });
      setActivePreset(CUSTOM_PRESET_ID);
      setAwaitingTo(true);
      return;
    }

    const range = normalizeRange(draftValue.from, selected);
    setDraftValue({ preset: CUSTOM_PRESET_ID, ...range });
    setAwaitingTo(false);
  };

  const applyCustomRange = () => {
    if (!hasCompleteRange(draftValue)) return;

    commitValue({ preset: CUSTOM_PRESET_ID, from: draftValue.from, to: draftValue.to });
    setOpen(false);
  };

  const cancelCustomRange = () => {
    setDraftValue(committedValue);
    setActivePreset(committedValue.preset ?? (hasCompleteRange(committedValue) ? CUSTOM_PRESET_ID : ""));
    setAwaitingTo(false);
    setHoveredDate(undefined);
    setOpen(false);
  };

  const clearValue = () => {
    commitValue({});
    setDraftValue({});
    setActivePreset("");
    setAwaitingTo(false);
    setOpen(false);
  };

  const hasDraftRange = hasCompleteRange(draftValue);
  const showCustomPanel = activePreset === CUSTOM_PRESET_ID;
  const canApply = hasDraftRange;
  const showRangeLimit = infoText ?? (maxRangeDays ? `Date range can be selected for up to ${maxRangeDays} days.` : undefined);

  return (
    <div className={cn("bfrs-inline-flex bfrs-flex-col bfrs-gap-1.5", className)} {...props}>
      {label && <div className="bfrs-text-[12px] bfrs-font-semibold bfrs-text-text-primary">{label}</div>}
      <RadixPopover.Root open={open} onOpenChange={setOpen}>
        <RadixPopover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "bfrs-inline-flex bfrs-w-full bfrs-min-w-0 bfrs-items-center bfrs-justify-between bfrs-gap-2 bfrs-rounded-[var(--bfrs-button-radius)] bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-text-[12px] bfrs-font-normal bfrs-text-text-primary bfrs-outline-none bfrs-transition bfrs-duration-150 bfrs-ease-out hover:bfrs-bg-primary-soft active:bfrs-bg-surface-muted focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25 disabled:bfrs-pointer-events-none disabled:bfrs-opacity-55",
              size === "md" ? "bfrs-h-9 bfrs-px-3" : "bfrs-h-7 bfrs-px-3",
              !hasCompleteRange(committedValue) && !committedValue.preset ? "bfrs-text-text-muted" : "bfrs-text-text-primary",
              size === "sm" && (hasCompleteRange(committedValue) || Boolean(committedValue.preset)) && "bfrs-border-border-focus",
              triggerClassName
            )}
          >
            <CalendarBlank size={13} className="bfrs-shrink-0 bfrs-text-text-muted" />
            <span className="bfrs-flex-1 bfrs-truncate bfrs-text-left">{triggerLabel}</span>
            <CaretDown size={10} weight="bold" className={cn("bfrs-shrink-0 bfrs-text-text-muted bfrs-transition-transform bfrs-duration-150", open && "bfrs-rotate-180")} />
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <RadixPopover.Content
              align={align}
              side={side}
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              className={cn(
                floatingLayerClassName,
                "bfrs-flex bfrs-max-h-[min(760px,var(--radix-popover-content-available-height))] bfrs-flex-col bfrs-overflow-hidden bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-elevated bfrs-outline-none bfrs-animate-content-in",
                showCustomPanel ? "bfrs-w-[min(calc(100vw-1rem),700px)]" : "bfrs-w-56",
                contentClassName
              )}
            >
              <div className={cn("bfrs-flex bfrs-min-h-0 bfrs-flex-1 bfrs-overflow-auto bfrs-[scrollbar-width:none] bfrs-[-ms-overflow-style:none] [&::-webkit-scrollbar]:bfrs-hidden", showCustomPanel && "bfrs-flex-col md:bfrs-flex-row")}>
                <div className={cn("bfrs-flex bfrs-w-full bfrs-shrink-0 bfrs-flex-col bfrs-border-border", showCustomPanel && "bfrs-bg-surface-card md:bfrs-w-[144px] md:bfrs-border-r")}>
                  <div className="bfrs-flex-1 bfrs-py-2">
                    {presets.map((preset) => {
                      const selected = activePreset === preset.id;

                      return (
                        <button
                          key={preset.id}
                          type="button"
                          disabled={preset.disabled}
                          onClick={() => selectPreset(preset)}
                          className={cn(
                            "bfrs-flex bfrs-w-full bfrs-cursor-pointer bfrs-items-center bfrs-justify-between bfrs-gap-3 bfrs-text-left bfrs-text-[12px] bfrs-outline-none bfrs-transition hover:bfrs-bg-primary-soft focus-visible:bfrs-bg-primary-soft",
                            size === "md" ? "bfrs-h-9 bfrs-px-3" : "bfrs-h-7 bfrs-px-2.5",
                            selected ? "bfrs-bg-primary-soft bfrs-font-bold bfrs-text-text-primary" : "bfrs-font-medium bfrs-text-text-secondary",
                            preset.disabled && "bfrs-cursor-not-allowed bfrs-text-text-disabled bfrs-opacity-60 hover:bfrs-bg-transparent"
                          )}
                        >
                          <span>{preset.label}</span>
                          {selected && <Check size={14} weight="bold" />}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={startCustomSelection}
                      className={cn(
                        cn("bfrs-flex bfrs-w-full bfrs-cursor-pointer bfrs-items-center bfrs-justify-between bfrs-gap-3 bfrs-text-left bfrs-text-[12px] bfrs-outline-none bfrs-transition hover:bfrs-bg-primary-soft focus-visible:bfrs-bg-primary-soft", size === "md" ? "bfrs-h-9 bfrs-px-3" : "bfrs-h-7 bfrs-px-2.5"),
                        showCustomPanel ? "bfrs-bg-primary-soft bfrs-font-bold bfrs-text-text-primary" : "bfrs-font-medium bfrs-text-text-secondary"
                      )}
                    >
                      <span>{customPresetLabel}</span>
                      {showCustomPanel && <Check size={14} weight="bold" />}
                    </button>
                  </div>
                  {showRangeLimit && !showCustomPanel && (
                    <div className="bfrs-sticky bfrs-bottom-0 bfrs-shrink-0 bfrs-flex bfrs-gap-2 bfrs-border-t bfrs-border-border bfrs-bg-surface-card bfrs-px-4 bfrs-py-3 bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">
                      <span className="bfrs-mt-0.5 bfrs-flex bfrs-shrink-0">
                        <WarningAlertIcon />
                      </span>
                      <span>{showRangeLimit}</span>
                    </div>
                  )}
                </div>

                {showCustomPanel && (
                  <div className="bfrs-min-w-0 bfrs-flex-1 bfrs-bg-surface-card">
                    <div className="bfrs-flex bfrs-items-center bfrs-gap-4 bfrs-px-5 bfrs-py-2.5" style={warningSurfaceTextStyle}>
                      <span className="bfrs-flex-1 bfrs-text-[12.5px] bfrs-font-medium">
                        Please select the year you want to view data for
                      </span>
                      <select
                        value={leftCalendar.year}
                        onChange={(e) => setLeftCalendar((c) => ({ year: Number(e.target.value), month: c.month }))}
                        className="bfrs-shrink-0 bfrs-cursor-pointer bfrs-rounded-lg bfrs-px-2.5 bfrs-py-1 bfrs-text-sm bfrs-font-semibold bfrs-outline-none"
                        style={warningSurfaceTextStyle}
                      >
                        {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - 10 + i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div className={cn("bfrs-grid bfrs-gap-4 bfrs-overflow-x-auto bfrs-p-4", months === 2 ? "md:bfrs-grid-cols-2" : "bfrs-grid-cols-1")}>
                      <CalendarMonth
                        year={leftCalendar.year}
                        month={leftCalendar.month}
                        from={draftValue.from}
                        to={draftValue.to}
                        hovered={hoveredDate}
                        awaitingTo={awaitingTo}
                        isDateDisabled={isDateDisabled}
                        onSelectDate={selectDate}
                        onHoverDate={setHoveredDate}
                        onPrev={() => setLeftCalendar((current) => shiftMonth(current.year, current.month, -1))}
                        onNext={() => setLeftCalendar((current) => shiftMonth(current.year, current.month, 1))}
                        showPrev={true}
                        showNext={months !== 2}
                        className={calendarClassName}
                      />
                      {months === 2 && (
                        <CalendarMonth
                          year={rightCalendar.year}
                          month={rightCalendar.month}
                          from={draftValue.from}
                          to={draftValue.to}
                          hovered={hoveredDate}
                          awaitingTo={awaitingTo}
                          isDateDisabled={isDateDisabled}
                          onSelectDate={selectDate}
                          onHoverDate={setHoveredDate}
                          onPrev={() => setLeftCalendar((current) => shiftMonth(current.year, current.month, -1))}
                          onNext={() => setLeftCalendar((current) => shiftMonth(current.year, current.month, 1))}
                          showPrev={false}
                          showNext={true}
                          className={calendarClassName}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              {showCustomPanel && (
                <div className="bfrs-flex bfrs-shrink-0 bfrs-items-stretch bfrs-border-t bfrs-border-border">
                  {showRangeLimit && (
                    <div className="bfrs-flex bfrs-w-[144px] bfrs-shrink-0 bfrs-items-start bfrs-gap-2 bfrs-border-r bfrs-border-border bfrs-bg-surface-card bfrs-px-4 bfrs-py-3 bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">
                      <span className="bfrs-mt-0.5 bfrs-flex bfrs-shrink-0">
                        <WarningAlertIcon />
                      </span>
                      <span>{showRangeLimit}</span>
                    </div>
                  )}
                  <div className="bfrs-flex bfrs-flex-1 bfrs-flex-col bfrs-gap-3 bfrs-bg-surface-card bfrs-px-5 bfrs-py-4 sm:bfrs-flex-row sm:bfrs-items-center sm:bfrs-justify-between">
                    <div className="bfrs-text-[13px] bfrs-text-text-secondary">
                      <span className="bfrs-font-semibold bfrs-text-primary">Selected:</span>{" "}
                      <span className="bfrs-font-medium">{draftValue.from ? formatDate(draftValue.from) : "--/--/----"}</span>{" "}
                      <span className="bfrs-text-text-muted">To</span>{" "}
                      <span className="bfrs-font-medium">{draftValue.to ? formatDate(draftValue.to) : "--/--/----"}</span>
                    </div>
                    <div className="bfrs-flex bfrs-flex-wrap bfrs-justify-end bfrs-gap-2">
                      {clearable && (
                        <Button variant="ghost" size="sm" leftIcon={<X size={13} weight="bold" />} onClick={clearValue}>
                          {clearLabel}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={cancelCustomRange}>
                        {cancelLabel}
                      </Button>
                      <Button size="sm" disabled={!canApply} onClick={applyCustomRange}>
                        {applyLabel}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </RadixPopover.Content>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      {helperText && <div className="bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">{helperText}</div>}
    </div>
  );
}

DateRangePicker.displayName = "DateRangePicker";
